import { State } from 'src/replicant/State';
import { getBuruFriends } from '../friends/friends.getters';
import features from '../ruleset/features';
import {
  buddiesEligibilityThreshold,
  buddiesMaxDisplayedInvites,
  buddiesMaxParty,
  buddiesMilestoneBaseGems,
  buddiesMilestoneThresholds,
  buddiesEligibilityMultipliers,
  buddiesDummyPrefix,
  buddiesNudgeMaxPerEvent,
  buddiesNudgeRecipientCooldown,
  buddiesSchedule,
  buddiesUnlockLevel,
} from './buddies.ruleset';
import { BuddiesEvent } from './buddies.schema';

/**
 * Calculate total party score for an event (main player + all party members)
 */
function buddiesGetPartyScoreForEvent(event: BuddiesEvent): number {
  let score = event.score;
  for (const pid in event.party) {
    score += event.party[pid].score;
  }
  return score;
}

/**
 * Get the highest milestone reached (0-4) from a party score
 */
export function buddiesGetMilestone(partyScore: number): number {
  let milestone = 0;
  for (let i = 0; i < buddiesMilestoneThresholds.length; i++) {
    if (partyScore >= buddiesMilestoneThresholds[i]) milestone = i + 1;
  }
  return milestone;
}

/**
 * Count eligible members (main player + party members with score >= threshold)
 */
export function buddiesGetEligibleCount(event: BuddiesEvent): number {
  let count = event.score >= buddiesEligibilityThreshold ? 1 : 0;
  for (const pid in event.party) {
    if (event.party[pid].score >= buddiesEligibilityThreshold) count++;
  }
  return count;
}

/**
 * Get party size (1 for main player + party member count)
 */
export function buddiesGetPartySize(event: BuddiesEvent): number {
  return 1 + Object.keys(event.party).length;
}

/**
 * Get common analytics properties for a buddies event.
 * If eventId is provided, looks up that specific event; otherwise uses the current event.
 */
export function buddiesGetCommonProps(
  state: State,
  now: number,
  eventId?: string,
): {
  milestone: number;
  partySize: number;
  partySizeEligible: number;
  playerScore: number;
  partyScore: number;
} {
  const event = eventId
    ? state.bakingBuddies?.events[eventId]
    : buddiesGetEvent(state, now);

  const partyScore = event ? buddiesGetPartyScoreForEvent(event) : 0;

  return {
    milestone: buddiesGetMilestone(partyScore),
    partySize: event ? buddiesGetPartySize(event) : 1,
    partySizeEligible: event ? buddiesGetEligibleCount(event) : 0,
    playerScore: event?.score ?? 0,
    partyScore,
  };
}

/**
 * Check if the main player is eligible for rewards (score >= threshold)
 */
function buddiesIsPlayerEligible(event: BuddiesEvent): boolean {
  return event.score >= buddiesEligibilityThreshold;
}

/**
 * Check if the grand prize (final milestone) has been reached
 */
function buddiesIsGrandPrizeReached(event: BuddiesEvent): boolean {
  const partyScore = buddiesGetPartyScoreForEvent(event);
  return (
    partyScore >=
    buddiesMilestoneThresholds[buddiesMilestoneThresholds.length - 1]
  );
}

/**
 * Check if an event is over (time expired OR grand prize reached)
 */
function buddiesIsEventOver(
  event: BuddiesEvent,
  eventId: string,
  now: number,
): boolean {
  const scheduledEvent = buddiesSchedule.findEventByID(eventId);
  const isTimeExpired = !scheduledEvent || now >= scheduledEvent.end;
  return isTimeExpired || buddiesIsGrandPrizeReached(event);
}

/**
 * Get the base gem reward for the highest milestone reached
 */
function buddiesGetBaseReward(event: BuddiesEvent): number {
  const partyScore = buddiesGetPartyScoreForEvent(event);
  const milestone = buddiesGetMilestone(partyScore);
  if (milestone === 0) return 0;
  return buddiesMilestoneBaseGems[milestone - 1];
}

/**
 * Get the reward multiplier based on how many members are eligible
 */
function buddiesGetRewardMultiplier(event: BuddiesEvent): number {
  const eligibleCount = buddiesGetEligibleCount(event);
  return buddiesEligibilityMultipliers[eligibleCount] ?? 1.0;
}

/**
 * Calculate total reward for an event (base gems * eligibility multiplier)
 */
function buddiesGetTotalReward(event: BuddiesEvent): number {
  return Math.floor(
    buddiesGetBaseReward(event) * buddiesGetRewardMultiplier(event),
  );
}

/**
 * Check if the player can claim rewards for an event.
 * Requires: milestone reached, player eligible, not already claimed, event over.
 */
function buddiesCanClaimRewards(
  event: BuddiesEvent,
  eventId: string,
  now: number,
): boolean {
  if (buddiesGetMilestone(buddiesGetPartyScoreForEvent(event)) === 0)
    return false;
  if (event.rewardsClaimed) return false;
  if (!buddiesIsPlayerEligible(event)) return false;
  if (!buddiesIsEventOver(event, eventId, now)) return false;
  return true;
}

/**
 * Check if the event is completed (grand prize reached).
 * When completed, no more invites should be sent or accepted.
 */
export function buddiesIsEventCompleted(state: State, now: number): boolean {
  const event = buddiesGetEvent(state, now);
  if (!event) return false;
  return buddiesIsGrandPrizeReached(event);
}

/**
 * Check if buddies is enabled for the user
 */
export function buddiesIsEnabled(state: State): boolean {
  return features(state).bakingBuddies;
}

/**
 * Check if the user has unlocked the buddies feature based on their level
 */
export function buddiesUnlocked(state: State): boolean {
  return state.match2.level >= buddiesUnlockLevel;
}

/**
 * Check if the feature should be available for the user
 */
export function buddiesIsAvailable(state: State, now: number): boolean {
  if (!buddiesIsEnabled(state)) {
    return false;
  }

  // V2: Level 20 unlock
  if (!buddiesUnlocked(state)) {
    return false;
  }

  // If there are pending rewards, the feature should be available
  if (buddiesGetPastEventWithPendingRewards(state, now)) {
    return true;
  }

  // Check if there is a current event
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return false;

  return true;
}

/**
 * Check if the buddies button should show in a locked state.
 * True when: user is enabled, has event state for the current event
 * (i.e., was invited), but level is below unlock threshold.
 */
export function buddiesIsLocked(state: State, now: number): boolean {
  if (!buddiesIsEnabled(state)) return false;
  if (state.match2.level >= buddiesUnlockLevel) return false;

  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return false;

  return !!state.bakingBuddies?.events[schedule.id];
}

/**
 * Return a past (non-current) event id with claimable rewards, if any.
 * The current event is always skipped — rewards are only claimable after it ends.
 */
export function buddiesGetPastEventWithPendingRewards(
  state: State,
  now: number,
): string | undefined {
  if (!state.bakingBuddies) return undefined;

  const currentEventId = buddiesSchedule.getCurrentEvent(now)?.id;

  for (const id of Object.keys(state.bakingBuddies.events)) {
    if (id === currentEventId) continue;
    if (!buddiesSchedule.findEventByID(id)) continue;
    const event = state.bakingBuddies.events[id];
    if (event && buddiesCanClaimRewards(event, id, now)) return id;
  }

  return undefined;
}

/**
 * Get the current event for the user
 */
export function buddiesGetEvent(
  state: State,
  now: number,
): BuddiesEvent | undefined {
  if (!state.bakingBuddies) return undefined;

  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return undefined;

  return state.bakingBuddies?.events[schedule.id];
}

/**
 * Whether the player was ever introduced to Buddies.
 *
 * `introducedAt` is the durable marker. Event records are also checked for
 * players introduced before the marker existed and not yet backfilled.
 */
export function buddiesHasSeenAnyEvent(state: State): boolean {
  if (state.bakingBuddies?.introducedAt) return true;
  const events = state.bakingBuddies?.events ?? {};
  return Object.values(events).some((event) => event.seenAt > 0);
}

/**
 * Check if the user party is already full
 */
export function buddiesIsPartyFull(state: State, now: number): boolean {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return true;
  const event = buddiesGetEvent(state, now);
  if (!event) return false;
  return Object.values(event.party).length >= buddiesMaxParty - 1; // -1 for the main player
}

/**
 * Check if the user can send or receive a buddies invite.
 * Requires: active event schedule, event not completed, party not full.
 */
export function buddiesCanSendOrReceiveInvite(
  state: State,
  now: number,
): boolean {
  if (!buddiesSchedule.getCurrentEvent(now)) return false;
  if (buddiesIsEventCompleted(state, now)) return false;
  if (buddiesIsPartyFull(state, now)) return false;
  return true;
}

export function buddiesHasPendingInvites(state: State, now: number): boolean {
  const event = buddiesGetEvent(state, now);
  if (!event) {
    return false;
  }
  if (buddiesIsPartyFull(state, now)) {
    return false;
  }
  return Object.values(event.invites).some(
    (invite) => invite.status === 'inviteReceived',
  );
}
/**
 * Get the total score of the party, including the main player
 */
export function buddiesGetPartyScore(state: State, now: number): number {
  const event = buddiesGetEvent(state, now);
  if (!event) return 0;
  const party = event.party;
  let score = event.score;
  for (const id in party) {
    score += party[id].score;
  }
  return score;
}

/**
 * Get the score added to the party since the last time the user saw the event
 */
export function buddiesGetPartyScoreAdded(state: State, now: number): number {
  const event = buddiesGetEvent(state, now);
  if (!event) return 0;
  const party = event.party;
  let score = event.lastScoreAdded ?? 0;
  for (const id in party) {
    score += party[id].lastScoreAdded ?? 0;
  }
  return score;
}

/**
 * Get the remaining time for the current event
 */
export function buddiesGetRemainingTime(now: number): number {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return 0;
  return schedule.end - now;
}

/**
 * Get the score to add based on puzzle attempts
 * (2 points for first attempt, 0 points otherwise)
 */
export function buddiesGetScoreByPuzzleAttempts(puzzleAttempts: number): 0 | 2 {
  return puzzleAttempts <= 1 ? 2 : 0;
}
/**
 * Check if user needs to see FTUE for the current event.
 * FTUE is required once per event instance.
 */
export function buddiesNeedsFTUE(state: State, now: number): boolean {
  const event = buddiesGetEvent(state, now);
  if (!event) return false;
  return !event.ftueSeenAt;
}

/**
 * Get the MVP (highest scoring entry) from a list.
 * Returns undefined if no entry meets the eligibility threshold.
 */
export function buddiesGetMVP<T extends { score: number }>(
  entries: T[],
): T | undefined {
  let mvp: T | undefined;
  for (const entry of entries) {
    if (
      entry.score >= buddiesEligibilityThreshold &&
      (!mvp || entry.score > mvp.score)
    ) {
      mvp = entry;
    }
  }
  return mvp;
}

/** Check if a player ID is a dummy buddy (debug cheat) */
export function buddiesIsDummyPlayer(id: string): boolean {
  return id.startsWith(buddiesDummyPrefix);
}

/**
 * Check if a party member can be nudged.
 * Conditions: must be in party, recipient cooldown (24h), sender max (2 per event).
 */
export function buddiesCanNudgePlayer(
  state: State,
  playerId: string,
  now: number,
): boolean {
  // Cannot nudge yourself or dummy players
  if (playerId === state.id) return false;
  if (buddiesIsDummyPlayer(playerId)) return false;

  const event = buddiesGetEvent(state, now);
  if (!event) return false;

  // Cannot nudge after event is completed (grand prize reached)
  if (buddiesIsGrandPrizeReached(event)) return false;

  // Must be in party
  const player = event.party[playerId];
  if (!player) return false;

  // Check sender's nudge count
  if (event.nudgesSentCount >= buddiesNudgeMaxPerEvent) {
    return false;
  }

  // Check recipient cooldown
  if (player.lastNudgedAt) {
    const timeSinceLastNudge = Math.max(0, now - player.lastNudgedAt);
    if (timeSinceLastNudge < buddiesNudgeRecipientCooldown) {
      return false;
    }
  }

  return true;
}

/**
 * Get IDs of players that should be excluded from invite suggestions.
 * Includes: self, current party members, invite senders/recipients, rejected suggestions, friends.
 */
export function buddiesGetExcludingIds(state: State, now: number): string[] {
  const event = buddiesGetEvent(state, now);
  const partyIds = event ? Object.keys(event.party) : [];
  const inviteIds = event ? Object.keys(event.invites) : [];
  const rejectedIds = Object.keys(
    state.bakingBuddies?.rejectedSuggestions ?? {},
  );
  const friendIds = getBuruFriends(state);
  return [state.id, ...partyIds, ...inviteIds, ...rejectedIds, ...friendIds];
}

/**
 * Get remaining cooldown time (ms) before a recipient can be nudged again.
 * Returns 0 if the recipient can be nudged now.
 * Used by #10129 (Active Feature UI) for the nudge button countdown display.
 */
export function buddiesGetNudgeCooldownRemaining(
  state: State,
  now: number,
  playerId: string,
): number {
  const event = buddiesGetEvent(state, now);
  if (!event) return 0;

  const player = event.party[playerId];
  if (!player?.lastNudgedAt) return 0;

  const remaining = buddiesNudgeRecipientCooldown - (now - player.lastNudgedAt);
  return Math.max(0, remaining);
}

/**
 * Get list of invite IDs to display (oldest first, up to limit).
 * Only returns invites with status 'inviteReceived'.
 */
export function buddiesGetDisplayedInvites(
  state: State,
  now: number,
): string[] {
  const event = buddiesGetEvent(state, now);
  if (!event) return [];

  const receivedInvites = Object.entries(event.invites)
    .filter(([_, invite]) => invite.status === 'inviteReceived')
    .sort(([, a], [, b]) => a.updatedAt - b.updatedAt);

  return receivedInvites.slice(0, buddiesMaxDisplayedInvites).map(([id]) => id);
}

/**
 * Get the number of party members that joined since the owner last acknowledged.
 * Returns 0 if no event exists or no new members have joined.
 */
export function buddiesGetUnseenPartyJoinCount(
  state: State,
  now: number,
): number {
  const event = buddiesGetEvent(state, now);
  if (!event) return 0;

  const currentPartySize = Object.keys(event.party).length;
  const lastSeenSize = event.lastSeenPartySize ?? 0;
  const effectiveLastSeenSize = Math.min(lastSeenSize, currentPartySize);

  return Math.max(0, currentPartySize - effectiveLastSeenSize);
}

/**
 * Get reward details for a specific event.
 * Returns milestone, eligibility, multiplier, reward amount, and event-over status.
 */
export function buddiesGetEventRewardDetails(
  state: State,
  eventId: string,
  now: number,
) {
  const event = state.bakingBuddies?.events?.[eventId];
  if (!event) return undefined;

  const partyScore = buddiesGetPartyScoreForEvent(event);

  return {
    milestone: buddiesGetMilestone(partyScore),
    partySize: buddiesGetPartySize(event),
    eligibleCount: buddiesGetEligibleCount(event),
    multiplier: buddiesGetRewardMultiplier(event),
    baseReward: buddiesGetBaseReward(event),
    totalReward: buddiesGetTotalReward(event),
    playerScore: event.score,
    partyScore,
    isPlayerEligible: buddiesIsPlayerEligible(event),
    isClaimed: event.rewardsClaimed,
    isEventOver: buddiesIsEventOver(event, eventId, now),
    canClaimRewards: buddiesCanClaimRewards(event, eventId, now),
  };
}

/**
 * Check if there is anything for the user to check in the buddies feature.
 * This includes: new score added, pending invites, claimable rewards, or ongoing event with claimable rewards.
 */
export function buddiesHasAnythingToCheck(state: State, now: number): boolean {
  if (buddiesGetPartyScoreAdded(state, now)) return true;
  if (buddiesHasPendingInvites(state, now)) return true;
  return buddiesGetCurrentStatus(state, now) === 'claim';
}

/** Possible HUD button statuses for the buddies feature */
export type BuddiesHudStatus = 'claim' | 'inactive' | 'inProgress';

/**
 * Get the current status of the buddies feature for the user.
 * Possible return values:
 * - 'inactive': no ongoing event and no pending rewards
 * - 'inProgress': ongoing event but user cannot claim rewards yet
 * - 'claim': user has pending rewards to claim or can claim rewards for the ongoing event
 */
export function buddiesGetCurrentStatus(
  state: State,
  now: number,
): BuddiesHudStatus {
  // Check if there are any rewards available to be claimed
  const pendingRewards = !!buddiesGetPastEventWithPendingRewards(state, now);
  if (pendingRewards) return 'claim';

  const event = buddiesSchedule.getCurrentEvent(now);
  if (!event) return 'inactive';

  const details = buddiesGetEventRewardDetails(state, event.id, now);
  if (!details) return 'inProgress';

  if (details.isClaimed) {
    return 'inactive';
  }

  if (details.canClaimRewards) {
    return 'claim';
  } else {
    return 'inProgress';
  }
}

/**
 * Possible statuses for a buddies event from the user's perspective.
 */
export type BuddiesEventStatus =
  | 'unavailable'
  | 'inProgress'
  | 'overPendingScore'
  | 'overPendingCollect'
  | 'overClaimed'
  | 'overNoClaim';

/**
 * Retrieve the status of a specific event for the user, based on reward details and event state.
 */
export function buddiesGetEventStatus(
  state: State,
  eventId: string,
  now: number,
): BuddiesEventStatus {
  const details = buddiesGetEventRewardDetails(state, eventId, now);

  // No details means either the event doesn't exist or the user has no score for it.
  if (!details) {
    const ongoingEventId = buddiesSchedule.getCurrentEvent(now)?.id;
    if (ongoingEventId === eventId) {
      return 'inProgress';
    } else {
      return 'unavailable';
    }
  }

  // Check if already claimed
  if (details.isClaimed) {
    return 'overClaimed';
  }

  // Find the event in user state to check if it exists and is over
  const event = state.bakingBuddies?.events?.[eventId];
  if (!event) {
    return 'unavailable';
  }

  // Check if time has expired (schedule-wise, independent of grand prize)
  const scheduledEvent = buddiesSchedule.findEventByID(eventId);
  const isTimeExpired = !scheduledEvent || now >= scheduledEvent.end;

  // Time expired and user cannot claim → nothing they can do
  if (isTimeExpired && !details.canClaimRewards) {
    return 'overNoClaim';
  }

  // Grand prize reached (but time NOT expired) and user not eligible →
  // user can still score to become eligible for rewards
  if (buddiesIsGrandPrizeReached(event) && !details.canClaimRewards) {
    return 'overPendingScore';
  }

  // User can claim rewards
  if (details.canClaimRewards) {
    return 'overPendingCollect';
  }

  // All checks passed, event must be in progress
  return 'inProgress';
}
